import { types as t, flow } from 'mobx-state-tree';
import { validateEmail, isValidSsn } from '@utils/validators';

import companyStore from './CompanyStore';
import { modalStore } from '../models/stores';
import ApplicationForm from './ApplicationForm';
import api from 'api';
import Job from './Job';

const ErrorType = t.model('ErrorType', {
  id: t.identifier,
  error: t.string,
});

export default t
  .model('JobApplication', {
    referralUuid: t.maybeNull(t.string),
    referredSite: t.maybeNull(t.string),
    name: t.optional(t.string, ''),
    ssn: t.optional(t.string, ''),
    phone: t.optional(t.string, ''),
    email: t.optional(t.string, ''),
    cv: t.optional(t.frozen(), null),
    coverLetter: t.optional(t.frozen(), null),
    errors: t.map(ErrorType),
    attachments: t.map(t.string),
    questions: ApplicationForm,
    job: Job,

    language: t.string,

    applying: false,
    disabled: false,
    loading: false,
    termsAccepted: false,
  })
  .views((self) => ({
    get hasErrors() {
      if (self.errors.size > 0) return true;

      const { sections = [] } = self.questions;
      let errors = false;
      for (let i = 0; i < sections.length; i++) {
        if (errors) {
          break;
        }
        let section = sections[i];
        for (let j = 0; j < section.questions.length; j++) {
          let question = section.questions[j];
          if (question.error) {
            errors = true;
            break;
          }
        }
      }
      return errors;
    },
    get nonFieldErrors() {
      const nonFieldError = self.errors.get('nonFieldErrors');
      return nonFieldError && nonFieldError.error;
    },
    get applicationFormData() {
      const data = new FormData();

      data.append('language', self.language);
      data.append('name', self.name);
      data.append('email', self.email);

      if (self.ssn) {
        data.append('ssn', self.ssn);
      }

      if (self.phone) {
        data.append('phone', self.phone);
      }

      if (self.cv !== null) {
        data.append('cv', self.cv);
      }

      if (self.coverLetter !== null) {
        data.append('coverLetter', self.coverLetter);
      }

      if (self.referralUuid) {
        data.append('referralUuid', self.referralUuid);
      }

      if (self.referredSite) {
        const isHttps = self.referredSite.startsWith('https://');
        const isHttp = self.referredSite.startsWith('http://');
        if (isHttps || isHttp) {
          data.append('referredSite', self.referredSite);
        }
      }

      const sections = self.questions.sections.map((section) => {
        return {
          id: section.id,
          questions: section.questions.map((question) => {
            if (question.type === 'attachment' && question.value) {
              if (question.multiple) {
                question.value.map((value, index) =>
                  data.append(index + '__' + question.id, value)
                );
              } else {
                data.append(question.id, question.value);
              }
            }
            return {
              id: question.id,
              type: question.type,
              title: question.title,
              multiple: question.multiple,
              value: question.type === 'attachment' ? null : question.value,
            };
          }),
        };
      });
      data.append('form', JSON.stringify({ sections }));
      return data;
    },
  }))
  .actions((self) => ({
    verifyJobFields() {
      const req = 'apply_error_required';
      const canRequire = ['cv', 'coverLetter', 'sex', 'ssn', 'phone'];
      let errors = false;
      canRequire.forEach((field) => {
        if (self.job[`require${field[0].toUpperCase() + field.substr(1)}`] && !self[field]) {
          errors = true;
          self.setError(field, req);
        }
      });

      if (!self.name) {
        errors = true;
        self.setError('name', req);
      }

      if (!self.email) {
        errors = true;
        self.setError('email', req);
      } else if (!validateEmail(self.email)) {
        errors = true;
        self.setError('email', 'apply_error_email');
      }

      if (companyStore.current.ssnValidation && self.ssn && !isValidSsn(self.ssn)) {
        errors = true;
        self.setError('ssn', 'apply_error_ssn');
      }
      return errors;
    },
    verifyFormQuestions() {
      self.questions.sections.forEach((section) => {
        section.questions.forEach((question) => {
          question.verify();
        });
      });
    },
    apply: flow(function* (showModal = true) {
      self.disabled = true;
      self.applying = true;
      self.verifyJobFields();
      self.verifyFormQuestions();

      if (self.hasErrors) {
        self.disabled = false;
        self.applying = false;
        return;
      }

      const res = yield api.applications.submitForm(
        companyStore.current.name,
        self.job.id,
        self.applicationFormData
      );
      if (!res.error) {
        if (showModal) {
          modalStore.show('APPLY_SUCCESS');
        }
        return true;
      } else {
        self.disabled = false;
        self.applying = false;
        const keys = res.data ? Object.keys(res.data) : [];
        if (keys.length > 0) {
          keys.forEach((key) => {
            self.setError(key, res.data[key][0]);
          });
        }
        return false;
      }
    }),
    set(field, value) {
      self[field] = value;
      if (self.errors.has(field)) {
        self.errors.delete(field);
      }
    },
    setError(id, error) {
      self.errors.put({ id, error });
    },
    clearError(id) {
      self.errors.delete(id);
    },
    toggleAcceptTerms() {
      self.termsAccepted = !self.termsAccepted;
    },
  }));
